import {useState, useCallback} from 'react';

import {useStores} from '../../../Hooks';
import {accessItemTypeEnum, runProcedureInEnum} from '../../../serverClientShared/sharedEnums';
import {
    cloneObj, DEFAULT_SETTINGS, buildSaveJson, saveProcedure,
    refreshProcedureInStores, generateAIInstructionsRequest,
    saveMirroredPermittedRoles, patchMirroredProcedureRoles,
} from './procedureSettingsHelpers';
import useAccessState from '../SettingsShared/useAccessState';
import useProcedureDirtyCheck from './useProcedureDirtyCheck';

const useProcedureSettings = () => {
    const {procedureStore, modalStore, projectStore, generalStore, dataStore} = useStores();
    const access = useAccessState();

    const [procedure, setProcedure] = useState(null);
    const [originalSnapshot, setOriginalSnapshot] = useState(null);
    const [editedName, setEditedName] = useState('');
    const [editedDescription, setEditedDescription] = useState('');
    const [editedAIInstructions, setEditedAIInstructions] = useState('');
    const [editedSettings, setEditedSettings] = useState({});
    const [editedThreeDSettings, setEditedThreeDSettings] = useState({});
    const [editedPermittedRoles, setEditedPermittedRoles] = useState({});
    const [editedThreeDModels, setEditedThreeDModels] = useState([]);
    const [editedAllowRun, setEditedAllowRun] = useState(null);
    const [editedCameraSpeed, setEditedCameraSpeed] = useState(0);
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);

    const initFromProcedure = useCallback((proc, isInitial = false) => {
        if (!proc) return;
        setProcedure(proc);
        if (isInitial) setOriginalSnapshot(cloneObj(proc));
        setEditedName(proc.ProcedureName || '');
        setEditedDescription(proc.ProcedureDescription || '');
        setEditedAIInstructions(proc.AIInstructions || '');
        setEditedSettings(cloneObj(proc.Settings) || DEFAULT_SETTINGS);
        setEditedThreeDSettings(cloneObj(proc.ThreeDSettings) || {});
        setEditedPermittedRoles(cloneObj(proc.PermittedTrainingAndFieldRoles) || {});
        setEditedThreeDModels([...(proc.ThreeDModelsConnected || [])]);
        setEditedAllowRun(proc.AllowRun || null);
        setEditedCameraSpeed(proc.Settings?.CameraMovementSpeedBetweenStep || 0);
    }, []);

    const isFieldsDirty = useProcedureDirtyCheck(
        procedure, originalSnapshot, editedName, editedDescription, editedAIInstructions,
        editedSettings, editedThreeDSettings, editedPermittedRoles,
        editedThreeDModels, editedAllowRun, editedCameraSpeed,
    );
    const isDirty = isFieldsDirty || access.isAccessDirty;

    const togglePlayerSetting = useCallback((key) => {
        setEditedSettings((prev) => ({...prev, [key]: !prev[key]}));
    }, []);

    const toggleThreeDSetting = useCallback((key) => {
        setEditedThreeDSettings((prev) => ({...prev, [key]: !prev[key]}));
    }, []);

    const updateThreeDLogo = useCallback((logoId) => {
        setEditedThreeDSettings((prev) => ({...prev, Logo: logoId}));
    }, []);

    const togglePermittedRole = useCallback((role) => {
        setEditedPermittedRoles((prev) => ({...prev, [role]: !prev[role]}));
    }, []);

    const toggleThreeDModel = useCallback((modelId) => {
        setEditedThreeDModels((prev) => {
            const next = prev.includes(modelId) ? prev.filter((id) => id !== modelId) : [...prev, modelId];
            if (!next.length) setEditedAllowRun(runProcedureInEnum.TWO_D);
            return next;
        });
    }, []);

    const updateCameraSpeed = useCallback((value) => {
        setEditedCameraSpeed(value);
        setEditedSettings((prev) => ({...prev, CameraMovementSpeedBetweenStep: value}));
    }, []);

    const executeMirroredSave = useCallback(async () => {
        const itemRef = {itemId: procedure.ProcedureIdentifier, itemType: accessItemTypeEnum.ITEM, nestedRoute: procedure.NestedFolderRoute || ''};
        await Promise.all([
            saveMirroredPermittedRoles(procedure.ProcedureIdentifier, editedPermittedRoles, generalStore.workspace),
            access.saveAccessChanges(generalStore.workspace, itemRef),
        ]);
        patchMirroredProcedureRoles(procedure.ProcedureIdentifier, editedPermittedRoles, {dataStore, procedureStore, modalStore, setProcedure});
    }, [procedure, editedPermittedRoles, generalStore, dataStore, procedureStore, modalStore, access]);

    const executeRegularSave = useCallback(async () => {
        const edited = {name: editedName, description: editedDescription, aiInstructions: editedAIInstructions, settings: editedSettings, threeDSettings: editedThreeDSettings, permittedRoles: editedPermittedRoles, threeDModels: editedThreeDModels, allowRun: editedAllowRun};
        const json = buildSaveJson(procedure, edited);
        const itemRef = {itemId: procedure.ProcedureIdentifier, itemType: accessItemTypeEnum.ITEM, nestedRoute: procedure.NestedFolderRoute || ''};
        const [updated] = await Promise.all([
            saveProcedure(json, generalStore.workspace),
            access.saveAccessChanges(generalStore.workspace, itemRef),
        ]);
        refreshProcedureInStores(updated, {dataStore, procedureStore, modalStore, setProcedure});
        procedureStore.getEditingVersions?.().then?.();
    }, [procedure, editedName, editedDescription, editedAIInstructions, editedSettings, editedThreeDSettings, editedPermittedRoles, editedThreeDModels, editedAllowRun, generalStore, dataStore, procedureStore, modalStore, access]);

    const executeSave = useCallback(async () => {
        generalStore.isLoaderOpen = true;
        try {
            if (procedure.importedFromWorkspace) await executeMirroredSave();
            else await executeRegularSave();
        } catch (e) {
            console.error('Failed to save procedure settings:', e);
            modalStore.showErrorMessage(generalStore.translate('problemEditingProcedure'));
        }
        generalStore.isLoaderOpen = false;
    }, [procedure, generalStore, modalStore, executeMirroredSave, executeRegularSave]);

    const save = useCallback(async () => {
        if (!procedure || !isDirty) return;
        if (hasNameConflict(editedName, procedure, projectStore, modalStore, generalStore)) return;
        await executeSave();
    }, [procedure, isDirty, editedName, projectStore, modalStore, generalStore, executeSave]);

    const generateAIInstructions = useCallback(async () => {
        if (!procedureStore.currentProcedure) return;
        setIsGeneratingAI(true);
        try {
            const result = await generateAIInstructionsRequest(
                procedureStore.currentProcedure, editedAIInstructions,
                generalStore.workspace, projectStore.currentProject?.ProjectIdentifier,
            );
            setEditedAIInstructions(result);
        } catch (e) {
            modalStore.showErrorMessage(generalStore.translate('problemGeneratingInstructions'));
        }
        setIsGeneratingAI(false);
    }, [procedureStore, editedAIInstructions, generalStore, projectStore, modalStore]);

    const discard = useCallback(() => {
        if (originalSnapshot) initFromProcedure(originalSnapshot);
        access.discardAccess();
    }, [originalSnapshot, initFromProcedure, access]);

    return {
        procedure, setProcedure, initFromProcedure,
        itemId: procedure?.ProcedureIdentifier,
        nestedRoute: procedure?.NestedFolderRoute,
        itemType: accessItemTypeEnum.ITEM,
        editedName, setEditedName, editedDescription, setEditedDescription,
        editedAIInstructions, setEditedAIInstructions,
        editedSettings, editedThreeDSettings, editedPermittedRoles,
        editedThreeDModels, editedAllowRun, setEditedAllowRun,
        editedCameraSpeed, updateCameraSpeed,
        togglePlayerSetting, toggleThreeDSetting, updateThreeDLogo, togglePermittedRole, toggleThreeDModel,
        isDirty, save, discard,
        isGeneratingAI, generateAIInstructions,
        ...access,
    };
};

const hasNameConflict = (editedName, procedure, projectStore, modalStore, generalStore) => {
    const changed = editedName !== procedure.ProcedureName;
    const conflict = changed && projectStore.checkProcedureSameName(editedName, procedure.ProcedureIdentifier);
    if (conflict) modalStore.showErrorMessage(generalStore.translate('saveSameNameProcedureError'));
    return conflict;
};

export default useProcedureSettings;
